import { useEffect, useState } from 'react'
import Hero from '../components/Hero'
import { useParams } from 'react-router-dom'
import DonationCard from '../components/DonationCard'
import { usePageTitle } from '../../shared/components/PageTitleContext'
import { useTranslation } from 'react-i18next'
import { useCart } from '../../util/CartContext'
import { fetchWithLanguage } from '../../util/fetchFunction'
import { languageUtil } from '../../util/languageUtils'
import { ErrorOverlay, Loading, LoadingOverlay } from '../../shared/components/Loading'
import SuccessModal from '../components/SuccessModal'
import CartModal from '../components/CartModal'

const ProjectSlug = () => {
    const { id } = useParams()
    const [project, setProject] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
    const [isCartModalOpen, setIsCartModalOpen] = useState(false)
    const { t } = useTranslation()

    const { cart, addDonationItem } = useCart()
    const { setTitle } = usePageTitle()
    const { language } = languageUtil()

    const onDonationChange = async data => {
        try {
            await addDonationItem({
                data,
                id,
                type: 'project',
                onSuccess: () => setIsSuccessModalOpen(true),
                onError: error => () => {
                    console.error('Donation failed:', error)
                    setError(`Donation failed: ${error}`)
                },
            })
        } catch (error) {
            setError(`Unexpected error: ${error}`)
            console.error('Unexpected error:', error)
        }
    }

    useEffect(() => {
        const fetchNews = async () => {
            await fetchWithLanguage(`get-project-detail/${id}`, setProject, setError, setLoading)
        }

        fetchNews()
    }, [id, language])

    useEffect(() => {
        if (project?.title) {
            setTitle(`Strive2Help - ${project.title || project.title_en}`)
        }
    }, [project?.title, setTitle])

    return (
        <div>
            {loading ? (
                <Loading />
            ) : (
                <div className='relative'>
                    {cart.isLoading && <LoadingOverlay />}
                    {error && <ErrorOverlay message={error} onClose={() => setError(null)} />}

                    <SuccessModal
                        isOpen={isSuccessModalOpen}
                        onClose={() => setIsSuccessModalOpen(false)}
                        onOpenCart={() => setIsCartModalOpen(true)}
                    />
                    <CartModal isOpen={isCartModalOpen} onClose={() => setIsCartModalOpen(false)} />

                    <Hero
                        title={project.title || project.title_en}
                        imageUrl={project.header_image}
                        size='medium'
                    />

                    <div className='relative flex flex-col mx-auto lg:flex-row max-w-7xl'>
                        {/* Background decorative elements */}
                        <div className='absolute top-0 left-0 w-64 h-64 -translate-x-32 -translate-y-32 rounded-full bg-gradient-to-br from-amber-100/20 to-yellow-100/20 blur-3xl'></div>
                        <div className='absolute bottom-0 right-0 translate-x-48 translate-y-48 rounded-full w-96 h-96 bg-gradient-to-br from-blue-100/10 to-purple-100/10 blur-3xl'></div>

                        {/* Left Column - Donation Card (Sticky) */}
                        <div className='w-full px-4 py-8 lg:w-[400px] xl:w-[450px] lg:sticky top-24 self-start'>
                            <DonationCard
                                id={id}
                                donationData={project}
                                onAmountChange={onDonationChange}
                            />
                        </div>

                        {/* Right Column - Content */}
                        <div className='flex-1 max-w-3xl px-4 py-8 mx-auto lg:px-8 lg:py-12'>
                            {/* Project Subtitle */}
                            {project.subtitle || project.subtitle_en ? (
                                <div className='mb-8 text-center lg:text-left'>
                                    <h2 className='relative inline-block text-3xl font-bold text-gray-900 lg:text-4xl'>
                                        <span className='relative z-10'>
                                            {project.subtitle || project.subtitle_en}
                                        </span>
                                        <span className='absolute left-0 w-full h-3 transform rounded-full -bottom-2 bg-gradient-to-r from-amber-200/50 to-yellow-200/50 -rotate-1'></span>
                                    </h2>
                                    <div className='w-20 h-1 mx-auto mt-4 rounded-full lg:mx-0 bg-gradient-to-r from-amber-400 to-yellow-400'></div>
                                </div>
                            ) : null}

                            {/* Project Description */}
                            <div className='mb-12'>
                                <div className='p-6 border shadow-sm lg:p-8 bg-gradient-to-br from-white to-amber-50/30 rounded-2xl border-amber-100'>
                                    <div className='flex items-start gap-3 mb-4'>
                                        <div className='w-2 h-2 mt-2 rounded-full bg-amber-400'></div>
                                        <h3 className='text-lg font-semibold text-gray-900'>
                                            {t('donation.about')}
                                        </h3>
                                    </div>
                                    <p className='text-lg leading-relaxed text-gray-700'>
                                        {project.description || project.description_en}
                                    </p>
                                </div>
                            </div>

                            {/* Additional Content */}
                            {project.additional_content && (
                                <div className='mb-12'>
                                    <div className='p-6 bg-white border border-gray-200 shadow-sm lg:p-8 rounded-2xl'>
                                        <div className='flex items-start gap-3 mb-6'>
                                            <div className='w-2 h-2 mt-2 bg-blue-400 rounded-full'></div>
                                            <h3 className='text-lg font-semibold text-gray-900'>
                                                {t('donation.project')}
                                            </h3>
                                        </div>
                                        <div className='prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-a:text-amber-600 hover:prose-a:text-amber-700'>
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        project.additional_content ||
                                                        project.additional_content_en,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Image Preview */}
                            {project.image_preview && (
                                <div className='mb-12'>
                                    <div className='relative overflow-hidden shadow-lg rounded-2xl group'>
                                        <img
                                            src={project.image_preview}
                                            alt={`${project.title || project.title_en}`}
                                            className='w-full h-auto transition-transform duration-700 group-hover:scale-105'
                                        />
                                        <div className='absolute inset-0 bg-gradient-to-t from-black/20 to-transparent'></div>
                                        {/* <div className='absolute px-3 py-1 text-sm text-white rounded-full bottom-4 left-4 bg-black/40 backdrop-blur-sm'>
                                            Project Preview
                                        </div> */}
                                    </div>
                                </div>
                            )}

                            {/* Project Journeys */}
                            {project.journeys && project.journeys.length > 0 && (
                                <section className='mt-16 lg:mt-20'>
                                    <div className='mb-10 text-center lg:text-left'>
                                        <h3 className='relative inline-block text-2xl font-bold text-gray-900 lg:text-3xl'>
                                            <span className='relative z-10'>
                                                {t('donation.journey')}
                                            </span>
                                            <span className='absolute left-0 w-full h-3 transform rounded-full -bottom-2 bg-gradient-to-r from-emerald-200/50 to-green-200/50 -rotate-1'></span>
                                        </h3>
                                        <p className='max-w-2xl mt-4 text-gray-600'>
                                            {t('donation.journeySubtitle')}
                                        </p>
                                    </div>

                                    <div className='relative'>
                                        {/* Vertical timeline line */}
                                        <div className='absolute top-0 bottom-0 w-1 transform rounded-full left-6 lg:left-1/2 lg:-translate-x-1/2 bg-gradient-to-b from-amber-300 via-yellow-400 to-green-400'></div>

                                        {project.journeys.map((journey, index) => (
                                            <div
                                                key={journey.id}
                                                className={`relative mb-12 lg:mb-16 ${
                                                    index % 2 === 0
                                                        ? 'lg:pr-1/2 lg:pl-8'
                                                        : 'lg:pl-1/2 lg:pr-8'
                                                }`}
                                            >
                                                {/* Timeline node */}
                                                <div className='absolute z-10 w-4 h-4 transform -translate-x-1/2 border-2 border-white rounded-full shadow-lg left-4 lg:left-1/2 lg:-translate-x-1/2 bg-gradient-to-r from-amber-500 to-yellow-500'></div>

                                                {/* Glow effect */}
                                                <div className='absolute w-8 h-8 transform -translate-x-1/2 rounded-full left-4 lg:left-1/2 lg:-translate-x-1/2 bg-amber-400/30 blur-sm'></div>

                                                {/* Journey card */}
                                                <div
                                                    className={`ml-12 lg:ml-0 p-6 bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300 ${
                                                        index % 2 === 0 ? 'lg:mr-8' : 'lg:ml-8'
                                                    }`}
                                                >
                                                    {/* Date badge */}
                                                    <div className='inline-flex items-center gap-2 px-4 py-1.5 mb-4 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full'>
                                                        <svg
                                                            className='w-4 h-4 text-gray-600'
                                                            fill='currentColor'
                                                            viewBox='0 0 20 20'
                                                        >
                                                            <path
                                                                fillRule='evenodd'
                                                                d='M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z'
                                                                clipRule='evenodd'
                                                            />
                                                        </svg>
                                                        <span className='text-sm font-medium text-gray-700'>
                                                            {journey.happened_at}
                                                        </span>
                                                    </div>

                                                    <h4 className='mb-2 text-xl font-bold text-gray-900'>
                                                        {journey.title || journey.title_en}
                                                    </h4>

                                                    {journey.subtitle || journey.subtitle_en ? (
                                                        <p className='mb-3 font-medium text-gray-600'>
                                                            {journey.subtitle ||
                                                                journey.subtitle_en}
                                                        </p>
                                                    ) : null}

                                                    <p className='mb-4 text-gray-700'>
                                                        {journey.description ||
                                                            journey.description_en}
                                                    </p>

                                                    {journey.image_url && (
                                                        <div className='mt-4 overflow-hidden rounded-lg'>
                                                            <img
                                                                src={journey.image_url}
                                                                alt={
                                                                    journey.title ||
                                                                    journey.title_en
                                                                }
                                                                className='object-cover w-full h-48 transition-transform duration-500 hover:scale-105'
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Call to Action Section */}
                            {/* <div className='p-8 mt-16 text-center border bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl border-amber-200'>
                                <h3 className='mb-3 text-2xl font-bold text-gray-900'>
                                    Ready to Make an Impact?
                                </h3>
                                <p className='max-w-2xl mx-auto mb-6 text-gray-700'>
                                    Your donation will directly support this project and help create
                                    lasting change.
                                </p>
                                <button
                                    onClick={() =>
                                        document
                                            .querySelector('.lg\\:sticky')
                                            ?.scrollIntoView({ behavior: 'smooth' })
                                    }
                                    className='inline-flex items-center gap-2 px-8 py-3 font-bold text-white transition-all duration-300 transform rounded-lg shadow-lg bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 hover:scale-105 hover:shadow-xl'
                                >
                                    <svg
                                        className='w-5 h-5'
                                        fill='none'
                                        stroke='currentColor'
                                        viewBox='0 0 24 24'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth='2'
                                            d='M12 8v8m-4-4h8m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                                        ></path>
                                    </svg>
                                    Donate Now
                                </button>
                            </div> */}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ProjectSlug
